// src/services/Integrations/DziIntegration.ts

import { CALCULATOR_BASE_URL } from "../../constants/apiBases.ts";

const BASE = `${CALCULATOR_BASE_URL}/dzi`;

const post = async <T>(jwt: string, path: string, body: object): Promise<T> => {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${jwt}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`ДЗИ ${path}: HTTP ${res.status}${text ? ` — ${text}` : ""}`);
  }
  return res.json();
};

const get = async <T>(jwt: string, path: string, params?: Record<string, string>): Promise<T> => {
  const url = new URL(`${BASE}${path}`);
  if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${jwt}` } });
  if (!res.ok) throw new Error(`ДЗИ ${path}: HTTP ${res.status}`);
  return res.json();
};

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DziOwner {
  pid: string;
  personType: "PhysicalPerson" | "LegalEntity";
  cityCode: string;
}

export interface DziVehicle {
  regNo: string;
  ocp12: string;            // certificate number
  covers: { coverType: string }[];
  chassis?: string;
  make?: string;
  model?: string;
  numSeats?: number;
  ccmTon?: number;
  enginePower?: number;
  prodYear?: number;
  carAge?: number;
  ocp17?: string;           // first registration date "YYYY-MM-DD"
  carUsage?: number;
  tonage?: number;
  regType?: string;
  carType?: string;
  carSubType?: string;
  carBodyType?: string;
  engineType?: string;
  driveType?: string;
  engine?: string;
  windowColor?: string;
  paintType?: string;
  prodType?: string;
}

export interface DziCalcPremiumRequest {
  clientPid: string;
  clientPersonType: "PhysicalPerson" | "LegalEntity";
  clientCityCode: string;
  owners: DziOwner[];
  questions: { questId: string; questAnswer: string }[];
  insuranceBegin: string;   // "YYYY-MM-DDTHH:MM:SS.000"
  insuranceEnd: string;
  numInstallments: number;
  vehicle: DziVehicle;
}

export interface DziCover {
  coverType: string;
  premium: number;
}

export interface DziPremiumVariant {
  variantId?: string;
  installmentOption: string;
  insuredObjects: { covers: DziCover[] }[];
}

export interface DziCalcPremiumResponse {
  /** The offer/application ID — used as policyID in convertOfferToPolicy */
  policyID?: number;
  offerId?: string;
  premiumVariants: DziPremiumVariant[];
}

export interface DziConvertOfferRequest {
  policyID: number;       // from calcPremium response
  annexID: number;        // 0 for new policies
  sticker: string;        // sticker serial number
  greenCard: string;      // green card serial number
  eventType: "APPL_CONV"; // always APPL_CONV
}

export interface DziConvertOfferResponse {
  policyNumber?: string;
  [key: string]: unknown;
}

export interface DziBmResponse {
  data: {
    gfRegNo?: string;
    gfChassis?: string;
    gfMake?: string;
    gfModel?: string;
    gfCmmTon?: number;
    gfSeatsNum?: number;
    gfEnginePower?: number;
    gfTonage?: number;
    gfRegType?: string;
    gfCarType?: string;
    gfCarSubType?: string;
    gfFuelType?: string;
    gfFirstRegDate?: string;
    prodYearBasedOnGF?: number;
    carAgeBasedOnGF?: number;
    gfOwners?: {
      pid: string;
      name: string;
      personType: string;
      isLeasing: "Y" | "N";
      addresses: { cityCode: string; address: string; addressType: string }[];
    }[];
    insisData?: {
      engineType?: string;
      driveType?: string;
      carbodyType?: string;
      engine?: string;
      usage?: number;
      windowColor?: string;
      paintType?: string;
      prodType?: string;
    }[];
  };
}

// ── Service ───────────────────────────────────────────────────────────────────

export const DziIntegrationService = {
  /** GET vehicle + owner data from GF registry */
  getBM: (jwt: string, registrationPlate: string, certificateNumber: string): Promise<DziBmResponse> =>
    post(jwt, "/Mtpl/getBM", { RegistrationPlate: registrationPlate, CertificateNumber: certificateNumber }),

  /** Calculate MTPL premium — returns premiumVariants */
  calcPremium: (jwt: string, payload: DziCalcPremiumRequest): Promise<DziCalcPremiumResponse> =>
    post(jwt, "/Mtpl/calcPremium", payload),

  /** Convert offer to issued policy — requires policyID from calcPremium response */
  convertOfferToPolicy: (jwt: string, payload: DziConvertOfferRequest): Promise<DziConvertOfferResponse> =>
    post(jwt, "/Mtpl/convertOfferToPolicy", payload),

  /** Download policy PDF — returns Blob */
  getDocumentsPdf: async (jwt: string, params: Record<string, string>): Promise<Blob> => {
    const url = new URL(`${BASE}/BIPublisher/getDocumentsPdf`);
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${jwt}` } });
    if (!res.ok) throw new Error(`ДЗИ печат: HTTP ${res.status}`);
    return res.blob();
  },

  /** Find person by PID */
  findPersonByPid: (jwt: string, pid: string): Promise<any> =>
    get(jwt, "/Mtpl/findpersonbypid", { pid }),

  /** Product configuration — car usage codes, etc. */
  getProductConfig: (jwt: string): Promise<any> =>
    get(jwt, "/Mtpl/productConfig"),
};
